import { graphql } from 'react-relay';
import React from 'react';
import { styled } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import { tokenToHex } from '../helpers.ts';

import type {
	SuggestedContentStateCustomizationAreaDeleteContentStateMutation as SuggestedContentStateCustomizationAreaDeleteContentStateMutation$data,
	SuggestedContentStateCustomizationAreaDeleteContentStateMutationVariables as SuggestedContentStateCustomizationAreaDeleteContentStateMutation$variables,
} from './__types__/SuggestedContentStateCustomizationAreaDeleteContentStateMutation.ts';
import type {
	SuggestedContentStateCustomizationAreaCreateSpaceContentStateMutation as SuggestedContentStateCustomizationAreaCreateSpaceContentStateMutation$data,
	SuggestedContentStateCustomizationAreaCreateSpaceContentStateMutationVariables as SuggestedContentStateCustomizationAreaCreateSpaceContentStateMutation$variables,
} from './__types__/SuggestedContentStateCustomizationAreaCreateSpaceContentStateMutation.ts';
import { SuggestedContentState } from './SuggestedContentState.tsx';
import { CreateContentStateButton } from './CreateContentStateButton.tsx';
import { isDuplicate } from './helpers.ts';
import type { ContentState, SuggestedContentStateCustomizationAreaProps } from './types.ts';
import { i18n } from './i18n.ts';
const MAX_SUGGESTED_STATES_DEFAULT = 5;
const MAX_SUGGESTED_STATES_EXPANDED = 10;
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SuggestedContentStateCustomizationAreaWrapper = styled.div({
	marginBottom: token('space.500'),
});
export const SuggestedContentStateCustomizationArea = withFlags(
	({
		spaceKey,
		showSuggested,
		suggestedStatesArray,
		setSuggestedStatesArray,
		additionalRows,
		flags,
	}: SuggestedContentStateCustomizationAreaProps) => {
		const maxSuggestedStates = fg('cc-increase-content-states')
			? MAX_SUGGESTED_STATES_EXPANDED
			: MAX_SUGGESTED_STATES_DEFAULT;
		const [createNewContentStateMutation] = useMutationInterop<
			SuggestedContentStateCustomizationAreaCreateSpaceContentStateMutation$data,
			SuggestedContentStateCustomizationAreaCreateSpaceContentStateMutation$variables
		>(
			graphql`
				mutation SuggestedContentStateCustomizationAreaCreateSpaceContentStateMutation(
					$contentState: ContentStateInput!
				) {
					createSpaceContentState(contentState: $contentState) {
						color
						name
						id
						restrictionLevel
					}
				}
			`,
			{
				onCompleted: (data) => {
					// Doing another setState after the mutation is completed so we can store the status ID.
					// Since the ID is generated in the backend, we didn't have an ID and therefore couldn't store it in the
					// initial set state in handleStateCreation() since we created a new status.
					const newState: ContentState | null = data.createSpaceContentState ?? null;
					const copyOfArrayWithoutLastItem = [...suggestedStatesArray];
					copyOfArrayWithoutLastItem.pop();
					const newArray: (ContentState | null)[] = [
						...copyOfArrayWithoutLastItem.filter(
							(item): item is ContentState | null => item !== undefined,
						),
						newState,
					];
					setSuggestedStatesArray(newArray);
				},
			},
		);
		const [deleteSpaceContentStateMutation] = useMutationInterop<
			SuggestedContentStateCustomizationAreaDeleteContentStateMutation$data,
			SuggestedContentStateCustomizationAreaDeleteContentStateMutation$variables
		>(graphql`
			mutation SuggestedContentStateCustomizationAreaDeleteContentStateMutation(
				$contentState: ContentStateInput!
			) {
				deleteContentState(stateInput: $contentState) {
					name
					color
					id
				}
			}
		`);
		const removeState = (color: string, name: string, id: string | null) => {
			if (color && name && id) {
				void deleteSpaceContentStateMutation({
					variables: {
						contentState: {
							spaceKey,
							color,
							name,
							id: id as any, // FIXME this problem always existed added typecasting as a part of codemods
						},
					},
				});
				// newArray is the original array with the removed page status filtered out
				const newArray: (ContentState | null)[] = [];
				for (const state of suggestedStatesArray) {
					if (state?.color !== color || state?.name !== name) {
						newArray.push(state);
					}
				}
				setSuggestedStatesArray(newArray);
			}
		};
		const handleStateCreation = (color: string, name: string) => {
			const newState = {
				color,
				name,
			};
			// only add to array if it is not a duplicate of another name
			if (!isDuplicate(color, name, suggestedStatesArray)) {
				void createNewContentStateMutation({
					variables: {
						contentState: { ...newState, color: tokenToHex(color), spaceKey },
					},
				});
				const newArray = [...suggestedStatesArray, newState];
				setSuggestedStatesArray(newArray);
			} else {
				// if duplicate status was added, show a flag warning user
				void flags.showWarningFlag({
					title: <FormattedMessage {...i18n.flagTitle} />,
					description: <FormattedMessage {...i18n.flagDescription} />,
				});
			}
		};
		// Build the merged list: additionalRows are partitioned once and inserted
		// at the correct position (before the first restricted state, or at the end).
		const rows = additionalRows ?? [];
		const prefixNodes: React.ReactNode[] = [];
		const suffixNodes: React.ReactNode[] = [];
		for (const row of rows) {
			const node = <React.Fragment key={row.key}>{row.node}</React.Fragment>;
			if (row.insertBeforeRestrictedState) {
				prefixNodes.push(node);
			} else {
				suffixNodes.push(node);
			}
		}

		const stateRows: React.ReactNode[] = [];
		let insertedPrefix = false;
		for (const state of suggestedStatesArray) {
			if (!insertedPrefix && state?.restrictionLevel && state.restrictionLevel !== 'NONE') {
				stateRows.push(...prefixNodes);
				insertedPrefix = true;
			}
			if (state) {
				stateRows.push(
					<SuggestedContentState
						// @ts-ignore
						key={state.color + state.name}
						color={state.color}
						spaceKey={spaceKey}
						name={state.name}
						restrictionLevel={state.restrictionLevel}
						id={state.id}
						removeState={removeState}
					/>,
				);
			}
		}
		if (!insertedPrefix) {
			stateRows.push(...prefixNodes);
		}
		stateRows.push(...suffixNodes);

		return showSuggested && suggestedStatesArray.length > 0 ? (
			<SuggestedContentStateCustomizationAreaWrapper data-testid="suggested-state-area-wrapper">
				{stateRows}
				<CreateContentStateButton
					spaceKey={spaceKey}
					onCreateState={handleStateCreation}
					isDisabled={suggestedStatesArray.length === maxSuggestedStates}
				/>
			</SuggestedContentStateCustomizationAreaWrapper>
		) : null;
	},
);
